import React, { useState, useEffect } from 'react';
import auth from '../AuthService';
import './Administration.css';

function Administration() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [requireTwoFactor, setRequireTwoFactor] = useState(true);
  const [toggling2FA, setToggling2FA] = useState(false);
  const [emailConfig, setEmailConfig] = useState(null);
  const [emailTestMsg, setEmailTestMsg] = useState('');
  const [refreshConfig, setRefreshConfig] = useState(null);

  // Create user form state
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    golfBookingsLogin: '',
    golfBookingsPassword: '',
    role: 'user',
    priority: 1000,
    bookingName: '',
  });

  // Edit user form state
  const [editForm, setEditForm] = useState({
    role: 'user',
    priority: 1000,
    password: '',
    bookingName: '',
  });

  useEffect(() => {
    fetchUsers();
    auth.fetch('/admin/require-2fa').then(data => {
      if (data && data.requireTwoFactor !== undefined) setRequireTwoFactor(data.requireTwoFactor);
    }).catch(() => {});
    auth.fetch('/admin/email-config').then(data => {
      if (data) setEmailConfig(data);
    }).catch(() => {});
    auth.fetch('/admin/auto-refresh-config').then(data => {
      if (data) setRefreshConfig(data);
    }).catch(() => {});
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await auth.fetch('/admin/users');
      setUsers(res);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await auth.fetch('/admin/users', {
        method: 'POST',
        body: JSON.stringify(newUser),
      });
      setNewUser({
        username: '',
        email: '',
        password: '',
        golfBookingsLogin: '',
        golfBookingsPassword: '',
        role: 'user',
        priority: 1000,
        bookingName: '',
      });
      setShowCreate(false);
      fetchUsers();
    } catch (err) {
      setError(err.message || 'Failed to create user');
    }
  };

  const openEdit = (user) => {
    setEditForm({
      role: user.role,
      priority: user.priority,
      password: '',
      bookingName: user.bookingName || '',
    });
    setEditingUser(user);
  };

  const handleUpdateUser = async (username) => {
    try {
      const body = {
        role: editForm.role,
        priority: editForm.priority,
      };
      if (editForm.password) {
        body.password = editForm.password;
      }
      if (editForm.bookingName) {
        body.bookingName = editForm.bookingName;
      }
      await auth.fetch(`/admin/users/${username}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      setError(err.message || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This cannot be undone.`)) return;
    try {
      await auth.fetch(`/admin/users/${username}`, {
        method: 'DELETE',
      });
      fetchUsers();
    } catch (err) {
      setError(err.message || 'Failed to delete user');
    }
  };

  const handleDisable2FA = async (username) => {
    if (!window.confirm(`Disable 2FA for "${username}"? They will be forced to set it up again on next login.`)) return;
    try {
      await auth.fetch(`/admin/users/${username}/disable-2fa`, {
        method: 'POST',
      });
      fetchUsers();
    } catch (err) {
      setError(err.message || 'Failed to disable 2FA');
    }
  };

  const handleToggle2FA = async () => {
    setToggling2FA(true);
    try {
      const res = await auth.fetch('/admin/require-2fa', {
        method: 'POST',
        body: JSON.stringify({ requireTwoFactor: !requireTwoFactor }),
      });
      if (res) setRequireTwoFactor(res.requireTwoFactor);
    } catch (err) {
      setError(err.message || 'Failed to toggle 2FA enforcement');
    }
    setToggling2FA(false);
  };

  const handleRevokeAppPassword = async (username) => {
    if (!window.confirm(`Revoke bot password for "${username}"? Their satellite bot will need a new password.`)) return;
    try {
      await auth.fetch(`/admin/users/${username}/revoke-app-password`, {
        method: 'POST',
      });
      fetchUsers();
    } catch (err) {
      setError(err.message || 'Failed to revoke app password');
    }
  };

  return (
    <div className="admin-page">
      <h3>Administration</h3>
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="admin-actions d-flex align-items-center gap-3 mb-3">
        <button className="btn btn-primary" onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? 'Cancel' : 'Create User'}
        </button>
        <button className="btn btn-outline-secondary" onClick={fetchUsers}>
          Refresh
        </button>
        <div className="form-check form-switch ms-auto">
          <label className="form-check-label">
            <input
              type="checkbox"
              className="form-check-input"
              checked={requireTwoFactor}
              onChange={handleToggle2FA}
              disabled={toggling2FA}
            />
            {' '}Require 2FA
          </label>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreate && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create New User</h5>
                <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
              </div>
              <form onSubmit={handleCreateUser}>
                <div className="modal-body">
                  <div className="row mb-2">
                    <div className="col">
                      <input className="form-control" placeholder="Username" value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} required />
                    </div>
                    <div className="col">
                      <input className="form-control" placeholder="Email" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} required />
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col">
                      <input className="form-control" placeholder="Password" type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required />
                    </div>
                    <div className="col">
                      <select className="form-control" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div className="col">
                      <input className="form-control" placeholder="Priority (ms)" type="number" min="0" value={newUser.priority} onChange={(e) => setNewUser({ ...newUser, priority: parseInt(e.target.value) || 0 })} />
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col">
                      <input className="form-control" placeholder="Player Name (e.g. John Smith)" value={newUser.bookingName} onChange={(e) => setNewUser({ ...newUser, bookingName: e.target.value })} />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                  <button type="submit" className="btn btn-success">Create User</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit User: {editingUser.username}</h5>
                <button type="button" className="btn-close" onClick={() => setEditingUser(null)}></button>
              </div>
              <div className="modal-body">
                <div className="row mb-2">
                  <div className="col">
                    <select className="form-control" value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="col">
                    <input className="form-control" placeholder="Priority (ms)" type="number" min="0" value={editForm.priority} onChange={(e) => setEditForm({ ...editForm, priority: parseInt(e.target.value) || 0 })} />
                  </div>
                  <div className="col">
                    <input className="form-control" placeholder="New Password (optional)" type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} />
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col">
                    <input className="form-control" placeholder="Player Name (e.g. John Smith)" value={editForm.bookingName} onChange={(e) => setEditForm({ ...editForm, bookingName: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setEditingUser(null)}>Cancel</button>
                <button className="btn btn-success" onClick={() => handleUpdateUser(editingUser.username)}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auto-Refresh Configuration */}
      {refreshConfig && (
        <div className="card p-3 mb-3">
          <h5>Event Auto-Refresh</h5>
          <div className="row mb-2 align-items-center">
            <div className="col-auto">
              <div className="form-check">
                <input type="checkbox" className="form-check-input" checked={refreshConfig.enabled} onChange={(e) => setRefreshConfig({ ...refreshConfig, enabled: e.target.checked })} id="refreshEnabled" />
                <label className="form-check-label" htmlFor="refreshEnabled">Enabled</label>
              </div>
            </div>
            <div className="col-auto">
              <input className="form-control" type="number" min="1" value={refreshConfig.intervalHours} onChange={(e) => setRefreshConfig({ ...refreshConfig, intervalHours: parseInt(e.target.value) || 6 })} style={{ width: '100px' }} />
            </div>
            <div className="col-auto">
              <small className="text-muted">hours between refreshes</small>
            </div>
            <div className="col-auto">
              <button className="btn btn-sm btn-outline-primary" onClick={async () => {
                await auth.fetch('/admin/auto-refresh-config', { method: 'PUT', body: JSON.stringify(refreshConfig) });
                alert('Auto-refresh config saved');
              }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Email Configuration */}
      {emailConfig && (
        <div className="card p-3 mb-3">
          <h5>Email Notifications</h5>
          <div className="row mb-2">
            <div className="col">
              <input className="form-control" placeholder="SMTP Host" value={emailConfig.host} onChange={(e) => setEmailConfig({ ...emailConfig, host: e.target.value })} />
            </div>
            <div className="col">
              <input className="form-control" placeholder="Port" type="number" value={emailConfig.port} onChange={(e) => setEmailConfig({ ...emailConfig, port: parseInt(e.target.value) || 587 })} />
            </div>
          </div>
          <div className="row mb-2">
            <div className="col">
              <input className="form-control" placeholder="Username" value={emailConfig.user} onChange={(e) => setEmailConfig({ ...emailConfig, user: e.target.value })} />
            </div>
            <div className="col">
              <input className="form-control" placeholder="Password" type="password" value={emailConfig.pass || ''} onChange={(e) => setEmailConfig({ ...emailConfig, pass: e.target.value })} />
            </div>
          </div>
          <div className="row mb-2">
            <div className="col">
              <input className="form-control" placeholder="From Address (optional)" value={emailConfig.from || ''} onChange={(e) => setEmailConfig({ ...emailConfig, from: e.target.value })} />
            </div>
            <div className="col d-flex align-items-center gap-2">
              <div className="form-check">
                <input type="checkbox" className="form-check-input" checked={emailConfig.enabled} onChange={(e) => setEmailConfig({ ...emailConfig, enabled: e.target.checked })} id="emailEnabled" />
                <label className="form-check-label" htmlFor="emailEnabled">Enabled</label>
              </div>
              <button className="btn btn-sm btn-outline-primary" onClick={async () => {
                await auth.fetch('/admin/email-config', {
                  method: 'PUT',
                  body: JSON.stringify(emailConfig),
                });
                setEmailTestMsg('Saved');
                setTimeout(() => setEmailTestMsg(''), 2000);
              }}>Save</button>
              <input type="email" className="form-control form-control-sm" placeholder="Send test to" value={emailConfig.testTo || ''} onChange={(e) => setEmailConfig({ ...emailConfig, testTo: e.target.value })} style={{ maxWidth: '200px' }} />
              <button className="btn btn-sm btn-outline-info" onClick={async () => {
                await auth.fetch('/admin/email-config', { method: 'PUT', body: JSON.stringify(emailConfig) });
                const res = await auth.fetch('/admin/email-test', { method: 'POST', body: JSON.stringify({ to: emailConfig.testTo }) });
                setEmailTestMsg(res.message || 'Test email sent');
                setTimeout(() => setEmailTestMsg(''), 3000);
              }}>Test</button>
              {emailTestMsg && <small className="text-success">{emailTestMsg}</small>}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="admin-loading">Loading...</div>
      ) : (
        <table className="table table-striped admin-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Player Name</th>
              <th>Role</th>
              <th>Priority</th>
              <th>2FA</th>
              <th>Bot Password</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.username}>
                <td>{user.username}</td>
                <td>{user.email}</td>
                <td>{user.bookingName || ''}</td>
                <td>
                  <span className={user.role === 'admin' ? 'badge bg-danger' : 'badge bg-secondary'}>
                    {user.role}
                  </span>
                </td>
                <td>{user.priority}ms</td>
                <td>{user.twoFactorEnabled ? 'Enabled' : 'Disabled'}</td>
                <td>{user.hasAppPassword ? 'Yes' : 'No'}</td>
                <td className="admin-actions-cell">
                  <button className="btn btn-sm btn-outline-primary me-1" onClick={() => openEdit(user)}>Edit</button>
                  <button className="btn btn-sm btn-outline-warning me-1" onClick={() => handleDisable2FA(user.username)} disabled={!user.twoFactorEnabled}>2FA</button>
                  <button className="btn btn-sm btn-outline-info me-1" onClick={() => handleRevokeAppPassword(user.username)} disabled={!user.hasAppPassword}>Bot PW</button>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteUser(user.username)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default Administration;
